import { ComponentChildren, ComponentProps } from 'preact';
import { useState, useCallback, MutableRef, useRef, useEffect, useId } from 'preact/hooks';
import {
  keyExtractor,
  getFirstVisibleKey,
  getKey,
  getPrevNextKeyUsingRef,
  findElementByKey
} from '../utils/PRIVATE_collectionUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { MenuProps } from '../hooks/PRIVATE_useVisContextMenu/util';
import { useContextMenuGesture } from '../hooks/UNSAFE_useContextMenuGesture';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { Menu } from '../UNSAFE_Menu';
import { getDefaultContextMenu } from '../UNSAFE_TabBar/TabBarContextMenu';
import { TabBarContextMenuConfig, TabBarItemContextMenuContext } from '../UNSAFE_TabBarCommon';
import { NavigationListContextProps } from '../UNSAFE_NavigationListCommon';

const ITEM_SELECTOR = '[role="tab"]';
export const REMOVE_ICON_SELECTOR = '[data-oj-navigationlist-item-remove-icon]';
const TOUCH_OFFSET_VALUE = 40;
const menuPropGestureStates: Record<
  'mouse' | 'keyboard' | 'touch',
  Omit<Required<MenuProps>, 'anchorRef' | 'isOpen'>
> = {
  mouse: {
    initialFocus: 'menu',
    placement: 'bottom-start',
    offsetValue: 0
  },
  keyboard: {
    initialFocus: 'firstItem',
    placement: 'bottom-start',
    offsetValue: 0
  },
  touch: {
    initialFocus: 'menu',
    placement: 'end',
    offsetValue: TOUCH_OFFSET_VALUE
  }
};

/**
 * type for payload of current key change event handler
 */
type CurrentKeyDetail<K> = {
  value: K;
};
/**
 * type for payload of selection change event handler
 */
type SelectionDetail<K> = {
  value: K;
  reason: 'pointer' | 'keyboard';
};
/**
 * type for payload of remove event handler
 */
type RemoveDetail<K> = {
  value: K;
};

const initialMenuProps: MenuProps = {
  isOpen: false,
  initialFocus: 'menu',
  placement: 'bottom-start',
  offsetValue: 0,
  anchorRef: { current: null }
};

/*TODO: JET-58534. Create a hook to share some logic between navlist and tabbar. Hook will be used inside this one. */
export function useNavigationList<K extends string | number>({
  containerRef,
  selection,
  onRemove,
  onSelectionChange,
  children,
  contextMenuConfig
}: {
  containerRef: MutableRef<HTMLDivElement>;
  selection?: K;
  onRemove?: <K extends string | number>(detail: RemoveDetail<K>) => void;
  onSelectionChange?: <K extends string | number>(detail: SelectionDetail<K>) => void;
  children: ComponentChildren;
  contextMenuConfig?: TabBarContextMenuConfig<K>;
}) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection);

  const onCurrentKeyChange = useCallback((detail: CurrentKeyDetail<K | undefined>) => {
    setCurrentKey(detail.value);
  }, []) as NavigationListContextProps<K>['onCurrentKeyChange'];

  const { currentKeyProps } = useCurrentKey(
    (element) =>
      onRemove
        ? (extractOnlyItemKey(element, ITEM_SELECTOR, REMOVE_ICON_SELECTOR) as K)
        : keyExtractor(element, ITEM_SELECTOR),
    false,
    false,
    getPrevNextKeyUsingRef(containerRef, currentKey, true, ITEM_SELECTOR),
    getPrevNextKeyUsingRef(containerRef, currentKey, false, ITEM_SELECTOR),
    undefined,
    undefined,
    currentKey,
    onCurrentKeyChange
  );

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(containerRef, [
    'Home',
    'End',
    'ArrowUp',
    'ArrowDown'
  ]);

  const onFocus = useCallback(() => {
    if (containerRef.current && currentKey === undefined) {
      const key = getFirstVisibleKey(containerRef.current, ITEM_SELECTOR);
      if (key) {
        setCurrentKey(key as K);
      }
    }
  }, [currentKey, containerRef]);

  const onFocusProps = { onFocus };
  const prevNavItems = useRef<K[]>();

  useEffect(() => {
    if (containerRef.current) {
      const navListItemKeys = Array.from(
        containerRef.current.querySelectorAll(ITEM_SELECTOR),
        (elem) => getKey(elem as HTMLElement) as K
      );
      if (currentKey != null && navListItemKeys.indexOf(currentKey) !== -1) {
        const navListItem = findElementByKey(
          containerRef.current,
          currentKey,
          ITEM_SELECTOR
        ) as any;
        if (navListItem.scrollIntoViewIfNeeded) {
          // for some browsers, we'll need the non-standard scrollIntoViewIfNeeded
          navListItem.scrollIntoViewIfNeeded();
        } else {
          navListItem.scrollIntoView({ block: 'nearest' });
        }
      } else {
        if (currentKey && navListItemKeys.indexOf(currentKey) === -1) {
          prevNavItems.current
            ? setCurrentKey(findNextCurrentKey(currentKey, navListItemKeys, prevNavItems.current))
            : setCurrentKey(navListItemKeys[0] as K);
        }
        prevNavItems.current = navListItemKeys;
      }
    }
  }, [children, currentKey, containerRef]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ((event.key === 'Home' || event.key === 'End') && containerRef.current && currentKey) {
        const navListItemKey = Array.from(
          containerRef.current.querySelectorAll(ITEM_SELECTOR),
          (elem) => getKey(elem as HTMLElement) as K
        );
        onCurrentKeyChange?.({
          value: navListItemKey[event.key === 'Home' ? 0 : navListItemKey.length - 1] as K
        });
      }
      if (currentKey && (event.key === 'Enter' || event.key === ' ')) {
        // Prevent default to avoid page scrolling on Space (and native actions on Enter)
        event.preventDefault();
        onSelectionChange?.({
          value: currentKey as K,
          reason: 'keyboard'
        });
      }
      if (currentKey && event.key === 'Delete') {
        onRemove?.({ value: currentKey as K });
      }
    },
    [currentKey, onSelectionChange, onRemove, onCurrentKeyChange, containerRef]
  );

  const [contextMenuContext, setContextMenuContext] = useState<TabBarItemContextMenuContext<K>>();

  const [menuProps, setMenuProps] = useState<MenuProps>(initialMenuProps);
  const [accStatusInfo, setAccStatusInfo] = useState<string>();

  const { triggerProps } = useContextMenuGesture(
    ({ gesture, anchor, target }) => {
      let anchorBasedOnGesture = anchor;
      let itemKey = currentKey as K;
      if (gesture === 'keyboard') {
        //We find the element where the position of the menu is going to be based of
        const elem = findElementByKey(
          containerRef.current as HTMLElement,
          currentKey as K,
          ITEM_SELECTOR
        );
        //We set the correct anchor
        anchorBasedOnGesture = elem ? elem : anchor;
      } else {
        //We search for the key using the target of the event
        const key = keyExtractor(target as HTMLElement, ITEM_SELECTOR);

        //On touch devices, the keyExtractor returns null because target of touch event is the entire tabbar. Hence we make use of currentkey since the context menu is always launched from selected item and its key is currentKey
        itemKey = key != null ? (key as K) : (currentKey as K);
      }
      let isRemovable = false;
      if (containerRef.current) {
        isRemovable = getItemAttribute(itemKey, containerRef.current, REMOVE_ICON_SELECTOR) != null;
      }
      const defaultContext = getDefaultContextMenu<K>({
        // For default menu items the existence of REMOVE_ICON_SELECTOR is the indicator that it is removable because we render the default menu in touch devices not the application.
        // Furthermore in corepack due to delegations onRemove is always defined as it is a even handler hence not a good check for isRemovable.
        isRemovable: isRemovable,
        handleRemove: onRemove,
        handleReorder: undefined,
        itemKey: itemKey,
        tabKeys: undefined,
        // As direction is only needed for reorder to switch the labels
        direction: undefined,
        itemLabels: {
          labelRemove: isRemovable ? translations.tabbar_labelRemove() : undefined
        },
        handleAccStatus: (detail) => {
          const removeItemLabel = getItemLabel(detail.removedKey!, containerRef.current!) as string;
          const removeStatus =
            isRemovable && detail.removedKey ? ` Removed ${removeItemLabel}` : '';

          setAccStatusInfo(removeStatus);
        }
      });
      setContextMenuContext(defaultContext);

      setMenuProps({
        ...menuPropGestureStates[gesture],
        anchorRef: { current: anchorBasedOnGesture },
        // This is set so that when no contextMenuConfig and when items is not removable we need not open it.
        isOpen: contextMenuConfig != null
      });
    },
    {
      isDisabled: !contextMenuConfig //If there is no a context menu renderer we disable the hook
    }
  );

  const handleCloseContextMenu = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      //Focus has to be set on the current target that was obtained
      //during context menu gesture when menu is dismissed or item is "selected"
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        setCurrentKey(contextMenuContext?.itemKey);
        containerRef.current?.focus();
      }
      // We close the menu.The only prop that matters here is isOpen
      setMenuProps({ ...initialMenuProps });
    },
    [containerRef, contextMenuContext?.itemKey]
  );

  const contextMenuDescriptionId = 'contextMenu_' + useId();
  const contextMenuDescription = translations.tabbar_labelContextMenu();

  return {
    showFocusRing,
    currentKey,
    onCurrentKeyChange,
    onKeyDown,
    menuProps: { ...menuProps, onClose: handleCloseContextMenu },
    contextMenuContext,
    contextMenuDescription,
    contextMenuDescriptionId,
    accStatusInfo,
    containerHandlers: mergeProps(focusRingProps, currentKeyProps, onFocusProps, triggerProps)
  };
}
// Utility that returns the value for a attribute related to the role="tab" item.
export const getItemAttribute = <K extends string | number>(
  itemKey: K,
  root: HTMLElement,
  attribute: string
) => {
  const navItem = findElementByKey(root, itemKey as K, ITEM_SELECTOR) as HTMLElement;
  if (navItem) {
    const tabBarItemAttribute = navItem.querySelector(attribute);
    return tabBarItemAttribute;
  }
  return;
};

// Utility that returns the visible inner text inside the role="tab" item.
const getItemLabel = <K extends string | number>(itemKey: K, root: HTMLElement) => {
  const tabBarItem = findElementByKey(root, itemKey as K, ITEM_SELECTOR) as HTMLElement;
  if (tabBarItem) {
    return tabBarItem.innerText;
  }
  return;
};

const extractOnlyItemKey = (element: HTMLElement, itemSelector: string, itemEliminator: string) => {
  const navigationListItem = element.closest(itemSelector);
  const removeButton = element.closest(itemEliminator);
  return navigationListItem?.contains(removeButton) ? null : keyExtractor(element, itemSelector);
};

const findNextCurrentKey = <K>(currentKey: K, currNavs: K[], prevNavs: K[]) => {
  const index = prevNavs.indexOf(currentKey);
  if (index > 0) {
    const nextIndex = index === prevNavs.length - 1 ? index - 1 : index + 1;
    const nextKey = prevNavs[nextIndex];
    if (currNavs.indexOf(nextKey) !== -1) {
      return nextKey;
    }
  }
  // update current key to be the first one if we can't find a suitable next key
  return currNavs[0];
};
